// components/districts/FilterPanel.tsx
import React from 'react';
import { Search, Filter } from 'lucide-react';
import { EfficiencyRating, FilterPanelProps, RiskLevel } from '../../types/districts.types';


const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  onReset,
  isExpanded,
  onToggle,
  activeFiltersCount
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Filter className="w-5 h-5 mr-2 text-blue-600" />
            District Filters
          </h3>
          <div className="flex items-center space-x-4">
            {activeFiltersCount > 0 && (
              <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                {activeFiltersCount} active
              </span>
            )}
            <button
              onClick={onToggle}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              {isExpanded ? 'Collapse' : 'Expand Filters'}
            </button>
          </div>
        </div>
      </div>
      
      <div className="px-6 py-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search districts by name or code..."
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            value={filters.searchTerm || ''}
            onChange={(e) => onFiltersChange({ ...filters, searchTerm: e.target.value })}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min Works</label>
              <input
                type="number"
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.minWorks || ''}
                onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    performanceRating: e.target.value as EfficiencyRating || undefined
                  })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Works</label>
              <input
                type="number"
                placeholder="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.maxWorks || ''}
                onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    riskLevel: (e.target.value as RiskLevel) || undefined 
                  })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min Cost (₹ Lakhs)</label>
              <input
                type="number"
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.minCost || ''}
                onChange={(e) => onFiltersChange({ 
                  ...filters, 
                  minCost: e.target.value ? Number(e.target.value) : undefined 
                })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Cost (₹ Lakhs)</label>
              <input
                type="number"
                placeholder="100000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.maxCost || ''}
                onChange={(e) => onFiltersChange({ 
                  ...filters, 
                  maxCost: e.target.value ? Number(e.target.value) : undefined 
                })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Min Progress (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.minProgress || ''}
                onChange={(e) => onFiltersChange({ 
                  ...filters, 
                  minProgress: e.target.value ? Number(e.target.value) : undefined 
                })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Performance Rating</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.performanceRating || ''}
                onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    performanceRating: e.target.value as EfficiencyRating || undefined
                  })}
              >
                <option value="">All Ratings</option>
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Average">Average</option>
                <option value="Poor">Poor</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Risk Level</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={filters.riskLevel || ''}
                onChange={(e) => onFiltersChange({ 
                    ...filters, 
                    riskLevel: (e.target.value as RiskLevel) || undefined 
                  })}
              >
                <option value="">All Risk Levels</option>
                <option value="Low">Low Risk</option>
                <option value="Medium">Medium Risk</option>
                <option value="High">High Risk</option>
              </select>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <button
              onClick={onReset}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Reset All Filters
            </button>
            <div className="text-sm text-gray-500">{activeFiltersCount} active filters</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;